import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileSignature, FilePlus2, AlertTriangle, Mail, BarChart3,
  Plus, Check, X, Send, Gavel, MailPlus, FileText, ShieldAlert, Loader2, Save
} from 'lucide-react';
import { formatINR as _fmtINR, formatDate as _fmtDate } from '../lib/format';
import ContractRiskScanner from '../components/ContractRiskScanner';
import { apiGet, apiPost } from '../lib/api';
import { useUrlTab } from '../lib/hooks/useUrlState';

const API = process.env.REACT_APP_BACKEND_URL;

// Contracts surface prefers "—" for null values (it's a register-style page
// where missing means "not yet captured") rather than a hard "₹0".
const formatINR = (v) => _fmtINR(v, { dashIfNull: true });

const TABS = [
  { id: 'loa', label: 'LOA / Contract', icon: FileText },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'vos', label: 'Variation Orders', icon: FilePlus2 },
  { id: 'claims', label: 'Claims', icon: FileSignature },
  { id: 'disputes', label: 'Disputes', icon: Gavel },
  { id: 'correspondence', label: 'Correspondence', icon: Mail },
  { id: 'risk', label: 'Contract Risk', icon: ShieldAlert },
];

export default function Contracts() {
  const [tab, setTab] = useUrlTab('tab', 'dashboard');
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');

  useEffect(() => {
    fetch(`${API}/api/projects`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setProjects(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-display uppercase tracking-[0.2em] text-slate-500">Billing & Contracts</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Contract & Claim Management</h1>
          <p className="text-sm text-slate-500 mt-1">Variation orders · Claims · Disputes · Contractual correspondence — full audit trail.</p>
        </div>
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="contracts-project"
        >
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
        </select>
      </div>

      <div className="border-b border-slate-200">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`contracts-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-md border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? 'text-[#3b4872] border-[#3b4872] bg-indigo-50/40'
                  : 'text-slate-600 border-transparent hover:text-[#0F172A] hover:bg-slate-50'
              }`}
            >
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'loa' && <LoaContractPanel projectId={project} projects={projects} />}
      {tab === 'dashboard' && <Dashboard projectId={project} />}
      {tab === 'vos' && <VariationOrders projectId={project} projects={projects} />}
      {tab === 'claims' && <Claims projectId={project} projects={projects} />}
      {tab === 'disputes' && <Disputes projectId={project} projects={projects} />}
      {tab === 'correspondence' && <Correspondence projectId={project} projects={projects} />}
      {tab === 'risk' && <ContractRiskScanner projectId={project} />}

    </div>
  );
}

// ============================ Dashboard ============================
function Dashboard({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/contracts/dashboard?project_id=${projectId}` : `${API}/api/contracts/dashboard`;
    fetch(url, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="text-center py-12 text-slate-500">Loading…</div>;
  const s = data?.summary || {};

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Total VOs" value={s.total_vos || 0} />
        <KPI label="Approved VOs" value={s.approved_vo_count || 0} sub={formatINR(s.approved_vo_value)} accent="emerald" />
        <KPI label="Pending VOs" value={s.pending_vo_count || 0} sub={formatINR(s.pending_vo_value)} accent="amber" />
        <KPI label="Open Claims" value={s.open_claims || 0} sub={formatINR(s.open_claim_value)} accent="orange" />
        <KPI label="Active Disputes" value={s.active_disputes || 0} accent={s.active_disputes ? 'red' : 'default'} />
        <KPI label="Correspondence" value={s.total_correspondence || 0} sub="Total logged" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <PanelList
          title="Pending VO Approvals"
          items={data?.pending_vos || []}
          emptyText="No VOs awaiting approval"
          render={(v) => (
            <div className="flex justify-between items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-slate-500">{v.vo_no}</div>
                <div className="text-sm font-medium truncate">{v.title}</div>
                <div className="text-xs text-slate-500">{v.project_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className={`font-bold ${v.value >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatINR(v.value)}</div>
                {v.time_impact_days ? <div className="text-xs text-slate-500">{v.time_impact_days > 0 ? '+' : ''}{v.time_impact_days}d</div> : null}
              </div>
            </div>
          )}
        />
        <PanelList
          title="Open Claims"
          items={data?.open_claims || []}
          emptyText="No open claims"
          render={(c) => (
            <div className="flex justify-between items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-slate-500">{c.claim_no} · {c.claim_type}</div>
                <div className="text-sm font-medium truncate">{c.title}</div>
                <div className="text-xs text-slate-500">{c.project_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-[#3b4872]">{formatINR(c.claim_value)}</div>
                {c.claim_days ? <div className="text-xs text-slate-500">+{c.claim_days}d</div> : null}
              </div>
            </div>
          )}
        />
      </div>

      {(data?.active_disputes || []).length > 0 && (
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-red-700 flex items-center gap-2">
              <AlertTriangle size={14} /> Active Disputes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.active_disputes.map(d => (
              <div key={d.id} className="flex justify-between items-start gap-3 py-2 border-b border-slate-100 last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-slate-500">{d.dispute_no} · {d.resolution_method}</div>
                  <div className="text-sm font-medium">{d.title}</div>
                  <div className="text-xs text-slate-500">{d.parties}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-red-700">{formatINR(d.dispute_value)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPI({ label, value, sub, accent = 'default' }) {
  const cls = {
    emerald: 'border-emerald-200 bg-emerald-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    orange: 'border-indigo-200 bg-indigo-50/50',
    red: 'border-red-300 bg-red-50',
    default: '',
  }[accent];
  return (
    <Card className={cls}>
      <CardContent className="p-3">
        <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</div>
        <div className="text-2xl font-display font-extrabold mt-1 tracking-tight">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function PanelList({ title, items, render, emptyText }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">{emptyText}</div>
        ) : (
          <div>{items.map((it, idx) => <div key={it.id || idx}>{render(it)}</div>)}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================ Variation Orders ============================
function VariationOrders({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (statusFilter) params.set('status', statusFilter);
    fetch(`${API}/api/variation-orders?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setItems(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId, statusFilter]);

  useEffect(load, [load]);

  const submit = async (id) => {
    const r = await fetch(`${API}/api/variation-orders/${id}/submit`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('VO submitted for approval'); load(); } else toast.error('Failed');
  };
  const approve = async (id) => {
    const r = await fetch(`${API}/api/variation-orders/${id}/approve`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('VO approved & contract value updated'); load(); } else toast.error('Failed');
  };
  const reject = async (id) => {
    const reason = prompt('Reason for rejection?');
    if (!reason) return;
    const r = await fetch(`${API}/api/variation-orders/${id}/reject?reason=${encodeURIComponent(reason)}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('VO rejected'); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'Draft', 'Pending Approval', 'Approved', 'Rejected'].map(s => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)}
              data-testid={`vo-filter-${s || 'all'}`}
              className={`text-xs px-3 py-1.5 rounded-sm border ${statusFilter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="vo-new-btn">
          <Plus size={14} className="mr-1.5" /> New VO
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">VO No</th>
                  <th className="text-left px-3 py-2.5">Project</th>
                  <th className="text-left px-3 py-2.5">Title</th>
                  <th className="text-center px-3 py-2.5">Type</th>
                  <th className="text-right px-3 py-2.5">Value</th>
                  <th className="text-center px-3 py-2.5">Days</th>
                  <th className="text-center px-3 py-2.5">Initiator</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                  items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-500">No VOs.</td></tr> :
                  items.map(v => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-indigo-50/30" data-testid={`vo-row-${v.vo_no}`}>
                      <td className="px-3 py-2 font-mono text-xs">{v.vo_no}</td>
                      <td className="px-3 py-2 text-xs">{v.project_name}</td>
                      <td className="px-3 py-2 max-w-md truncate" title={v.title}>{v.title}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm ${v.vo_type === 'Addition' ? 'bg-emerald-50 text-emerald-700' : v.vo_type === 'Deletion' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{v.vo_type}</span>
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${v.value >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatINR(v.value)}</td>
                      <td className="px-3 py-2 text-center text-xs">{v.time_impact_days > 0 ? `+${v.time_impact_days}` : v.time_impact_days || '—'}</td>
                      <td className="px-3 py-2 text-center text-xs">{v.initiator || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <StatusBadge status={v.status} />
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {v.status === 'Draft' && (
                          <button onClick={() => submit(v.id)} className="text-blue-600 hover:bg-blue-50 p-1 rounded mr-1" title="Submit" data-testid={`vo-submit-${v.vo_no}`}>
                            <Send size={13} />
                          </button>
                        )}
                        {v.status === 'Pending Approval' && (
                          <>
                            <button onClick={() => approve(v.id)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded mr-1" title="Approve" data-testid={`vo-approve-${v.vo_no}`}>
                              <Check size={14} />
                            </button>
                            <button onClick={() => reject(v.id)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Reject" data-testid={`vo-reject-${v.vo_no}`}>
                              <X size={14} />
                            </button>
                          </>
                        )}
                        {v.status === 'Approved' && <span className="text-[10px] text-slate-400">{v.approved_by}</span>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && <VOForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function VOForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', title: '', description: '', vo_type: 'Addition', value: 0, time_impact_days: 0, initiator: 'Client', justification: '' });
  const submit = async () => {
    if (!form.project_id || !form.title) { toast.error('Project and title required'); return; }
    const r = await fetch(`${API}/api/variation-orders`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ ...form, value: parseFloat(form.value) || 0, time_impact_days: parseInt(form.time_impact_days) || 0 }),
    });
    if (r.ok) { toast.success('VO created as Draft'); onSaved(); }
    else toast.error('Failed to create');
  };
  return (
    <Modal title="New Variation Order" onClose={onClose} onSubmit={submit} testId="vo-form">
      <Field label="Project">
        <select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>
          {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
        </select>
      </Field>
      <Field label="Title"><input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inp} /></Field>
      <Field label="Type">
        <select value={form.vo_type} onChange={(e) => setForm(f => ({ ...f, vo_type: e.target.value }))} className={inp}>
          {['Addition', 'Deletion', 'Substitution'].map(t => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Initiator">
        <select value={form.initiator} onChange={(e) => setForm(f => ({ ...f, initiator: e.target.value }))} className={inp}>
          {['Client', 'Contractor'].map(t => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Value (₹) — negative for deletions"><input type="number" value={form.value} onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))} className={inp} /></Field>
      <Field label="Time Impact (days)"><input type="number" value={form.time_impact_days} onChange={(e) => setForm(f => ({ ...f, time_impact_days: e.target.value }))} className={inp} /></Field>
      <Field label="Description" full><textarea rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={inp} /></Field>
      <Field label="Justification" full><textarea rows={2} value={form.justification} onChange={(e) => setForm(f => ({ ...f, justification: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Claims ============================
function Claims({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/claims?project_id=${projectId}` : `${API}/api/claims`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const updateStatus = async (id, status, settled_value, settled_days) => {
    const params = new URLSearchParams({ status });
    if (settled_value !== undefined) params.set('settled_value', settled_value);
    if (settled_days !== undefined) params.set('settled_days', settled_days);
    const r = await fetch(`${API}/api/claims/${id}/status?${params}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Claim updated'); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="claim-new-btn">
          <Plus size={14} className="mr-1.5" /> New Claim
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Claim No</th>
                <th className="text-left px-3 py-2.5">Project</th>
                <th className="text-left px-3 py-2.5">Title</th>
                <th className="text-center px-3 py-2.5">Type</th>
                <th className="text-right px-3 py-2.5">Claim Value</th>
                <th className="text-center px-3 py-2.5">Days</th>
                <th className="text-right px-3 py-2.5">Settled Value</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-500">No claims.</td></tr> :
                items.map(c => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`claim-row-${c.claim_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.claim_no}</td>
                    <td className="px-3 py-2 text-xs">{c.project_name}</td>
                    <td className="px-3 py-2 max-w-xs truncate">{c.title}</td>
                    <td className="px-3 py-2 text-center"><span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-sm">{c.claim_type}</span></td>
                    <td className="px-3 py-2 text-right font-bold text-[#3b4872]">{formatINR(c.claim_value)}</td>
                    <td className="px-3 py-2 text-center text-xs">{c.claim_days || '—'}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{c.settled_value !== null && c.settled_value !== undefined ? formatINR(c.settled_value) : '—'}</td>
                    <td className="px-3 py-2 text-center"><StatusBadge status={c.status} /></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!['Settled', 'Rejected'].includes(c.status) && (
                        <select className="text-xs border rounded px-2 py-0.5" data-testid={`claim-status-${c.claim_no}`}
                          value="" onChange={(e) => {
                            const v = e.target.value; if (!v) return;
                            if (v === 'Accepted' || v === 'Partially Accepted' || v === 'Settled') {
                              const sv = prompt('Settled value (₹)?', c.claim_value);
                              const sd = prompt('Settled days?', c.claim_days);
                              if (sv !== null) updateStatus(c.id, v, parseFloat(sv) || 0, parseInt(sd) || 0);
                            } else {
                              updateStatus(c.id, v);
                            }
                          }}>
                          <option value="">Update…</option>
                          {['Under Review', 'Accepted', 'Partially Accepted', 'Rejected', 'Settled'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <ClaimForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ClaimForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', claim_type: 'EOT', title: '', description: '', claim_value: 0, claim_days: 0, raised_against: 'Client' });
  const submit = async () => {
    if (!form.project_id || !form.title) { toast.error('Project and title required'); return; }
    const r = await fetch(`${API}/api/claims`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ ...form, claim_value: parseFloat(form.claim_value) || 0, claim_days: parseInt(form.claim_days) || 0 }),
    });
    if (r.ok) { toast.success('Claim created'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="New Claim" onClose={onClose} onSubmit={submit} testId="claim-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Type"><select value={form.claim_type} onChange={(e) => setForm(f => ({ ...f, claim_type: e.target.value }))} className={inp}>{['EOT', 'Compensation', 'Cost Overrun', 'Damages'].map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Title" full><input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inp} /></Field>
      <Field label="Claim Value (₹)"><input type="number" value={form.claim_value} onChange={(e) => setForm(f => ({ ...f, claim_value: e.target.value }))} className={inp} /></Field>
      <Field label="Claim Days"><input type="number" value={form.claim_days} onChange={(e) => setForm(f => ({ ...f, claim_days: e.target.value }))} className={inp} /></Field>
      <Field label="Description" full><textarea rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Disputes ============================
function Disputes({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/disputes?project_id=${projectId}` : `${API}/api/disputes`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const resolve = async (id) => {
    const summary = prompt('Resolution summary?');
    if (!summary) return;
    const r = await fetch(`${API}/api/disputes/${id}/resolve?resolution_summary=${encodeURIComponent(summary)}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Dispute resolved'); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="dispute-new-btn">
          <Plus size={14} className="mr-1.5" /> Log Dispute
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Dispute No</th>
                <th className="text-left px-3 py-2.5">Project</th>
                <th className="text-left px-3 py-2.5">Title</th>
                <th className="text-left px-3 py-2.5">Parties</th>
                <th className="text-right px-3 py-2.5">Value</th>
                <th className="text-center px-3 py-2.5">Method</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">No disputes.</td></tr> :
                items.map(d => (
                  <tr key={d.id} className="border-b border-slate-100" data-testid={`dispute-row-${d.dispute_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{d.dispute_no}</td>
                    <td className="px-3 py-2 text-xs">{d.project_name}</td>
                    <td className="px-3 py-2 max-w-xs">{d.title}</td>
                    <td className="px-3 py-2 text-xs">{d.parties || '—'}</td>
                    <td className="px-3 py-2 text-right font-bold text-red-700">{formatINR(d.dispute_value)}</td>
                    <td className="px-3 py-2 text-center text-xs">{d.resolution_method}</td>
                    <td className="px-3 py-2 text-center"><StatusBadge status={d.status} /></td>
                    <td className="px-3 py-2 text-right">
                      {d.status === 'Active' && (
                        <button onClick={() => resolve(d.id)} className="text-emerald-600 hover:bg-emerald-50 px-2 py-0.5 rounded text-xs border border-emerald-200" data-testid={`dispute-resolve-${d.dispute_no}`}>
                          <Check size={12} className="inline mr-1" />Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <DisputeForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function DisputeForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', title: '', description: '', dispute_value: 0, parties: '', resolution_method: 'Negotiation' });
  const submit = async () => {
    if (!form.project_id || !form.title) { toast.error('Project and title required'); return; }
    const r = await fetch(`${API}/api/disputes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ ...form, dispute_value: parseFloat(form.dispute_value) || 0 }),
    });
    if (r.ok) { toast.success('Dispute logged'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="Log Dispute" onClose={onClose} onSubmit={submit} testId="dispute-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Resolution Method"><select value={form.resolution_method} onChange={(e) => setForm(f => ({ ...f, resolution_method: e.target.value }))} className={inp}>{['Negotiation', 'Arbitration', 'Court', 'DAB'].map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Title" full><input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inp} /></Field>
      <Field label="Parties" full><input value={form.parties} onChange={(e) => setForm(f => ({ ...f, parties: e.target.value }))} className={inp} placeholder="e.g. MMRDA vs Ellora EPC" /></Field>
      <Field label="Dispute Value (₹)"><input type="number" value={form.dispute_value} onChange={(e) => setForm(f => ({ ...f, dispute_value: e.target.value }))} className={inp} /></Field>
      <Field label="Description" full><textarea rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Correspondence ============================
function Correspondence({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (categoryFilter) params.set('category', categoryFilter);
    fetch(`${API}/api/correspondence?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId, categoryFilter]);
  useEffect(load, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" data-testid="corr-category-filter">
          <option value="">All Categories</option>
          {['EOT Request', 'Notice', 'Reply', 'Site Instruction', 'General'].map(c => <option key={c}>{c}</option>)}
        </select>
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="corr-new-btn">
          <MailPlus size={14} className="mr-1.5" /> Log Correspondence
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-center px-3 py-2.5">Direction</th>
                <th className="text-left px-3 py-2.5">Ref No</th>
                <th className="text-left px-3 py-2.5">Project</th>
                <th className="text-left px-3 py-2.5">Subject</th>
                <th className="text-left px-3 py-2.5">Correspondent</th>
                <th className="text-center px-3 py-2.5">Category</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">No correspondence logged.</td></tr> :
                items.map(c => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`corr-row-${c.ref_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.sent_date}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm ${c.direction === 'Inward' ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'}`}>{c.direction}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{c.ref_no}</td>
                    <td className="px-3 py-2 text-xs">{c.project_name}</td>
                    <td className="px-3 py-2 max-w-md">
                      <div className="font-medium">{c.subject}</div>
                      {c.summary && <div className="text-xs text-slate-500 mt-0.5">{c.summary}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">{c.correspondent}</td>
                    <td className="px-3 py-2 text-center"><span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-sm">{c.category}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <CorrForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function CorrForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', ref_no: '', subject: '', direction: 'Outward', correspondent: '', category: 'General', sent_date: new Date().toISOString().slice(0, 10), summary: '' });
  const submit = async () => {
    if (!form.project_id || !form.subject || !form.ref_no) { toast.error('Project, ref no & subject required'); return; }
    const r = await fetch(`${API}/api/correspondence`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success('Correspondence logged'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="Log Correspondence" onClose={onClose} onSubmit={submit} testId="corr-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Direction"><select value={form.direction} onChange={(e) => setForm(f => ({ ...f, direction: e.target.value }))} className={inp}>{['Inward', 'Outward'].map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Reference No"><input value={form.ref_no} onChange={(e) => setForm(f => ({ ...f, ref_no: e.target.value }))} className={inp} /></Field>
      <Field label="Sent Date"><input type="date" value={form.sent_date} onChange={(e) => setForm(f => ({ ...f, sent_date: e.target.value }))} className={inp} /></Field>
      <Field label="Category"><select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className={inp}>{['EOT Request', 'Notice', 'Reply', 'Site Instruction', 'General'].map(c => <option key={c}>{c}</option>)}</select></Field>
      <Field label="Correspondent"><input value={form.correspondent} onChange={(e) => setForm(f => ({ ...f, correspondent: e.target.value }))} className={inp} /></Field>
      <Field label="Subject" full><input value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} className={inp} /></Field>
      <Field label="Summary" full><textarea rows={2} value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Shared ============================
function StatusBadge({ status }) {
  const map = {
    'Approved': 'bg-emerald-50 text-emerald-700',
    'Accepted': 'bg-emerald-50 text-emerald-700',
    'Settled': 'bg-emerald-50 text-emerald-700',
    'Resolved': 'bg-emerald-50 text-emerald-700',
    'Pending Approval': 'bg-amber-50 text-amber-700',
    'Submitted': 'bg-amber-50 text-amber-700',
    'Under Review': 'bg-blue-50 text-blue-700',
    'Partially Accepted': 'bg-blue-50 text-blue-700',
    'Draft': 'bg-slate-100 text-slate-600',
    'Active': 'bg-red-50 text-red-700',
    'Rejected': 'bg-red-50 text-red-700',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
}

const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40";

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-[11px] text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, onSubmit, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-3">{children}</div>
        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid={`${testId}-submit`}>Save</Button>
        </div>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// GAP 3 — LOA / Project Contract Panel
// ═══════════════════════════════════════════════════════════════
const CONTRACT_TYPES = ['item-rate', 'lump-sum', 'EPC (design-build)', 'cost-plus', 'BOT', 'other'];

function LoaContractPanel({ projectId, projects }) {
  const [contract, setContract] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    project_id: '', contract_type: 'item-rate', contract_no: '', loi_ref: '', loa_ref: '',
    award_date: '', start_date: '', completion_date: '', contract_value: '',
    dlp_months: 12, retention_pct: 5, retention_release_on_completion_pct: 50,
    ld_per_day: '', ld_cap_pct: 10, tds_pct: 1,
    mobilisation_advance_pct: 0, escalation_clause: '', payment_terms: '', notes: '',
  });

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const d = await apiGet(`/project-contracts/${projectId}`);
      setContract(d);
      setForm({ ...d, contract_value: d.contract_value || '' });
    } catch (e) {
      if (e?.response?.status === 404) { setContract(null); setForm((f) => ({ ...f, project_id: projectId })); }
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.project_id && !projectId) { toast.error('Select a project first'); return; }
    setSaving(true);
    try {
      await apiPost('/project-contracts', { ...form, project_id: form.project_id || projectId });
      toast.success('Contract record saved');
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children }) => (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const inp = (k, type = 'text', placeholder = '') => (
    <input type={type} value={form[k] || ''} onChange={upd(k)} disabled={!editing}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]" />
  );

  return (
    <div className="space-y-4" data-testid="loa-contract-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-base">LOA / Contract Details</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Base contract record — drives retention %, LD, DLP and payment defaults for RA bills and closeout.
          </div>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => { setEditing(true); if (!form.project_id) setForm((f) => ({ ...f, project_id: projectId || '' })); }}
              data-testid="contract-edit-btn">
              {contract ? 'Edit' : 'Set Up Contract'}
            </Button>
          )}
          {editing && (
            <>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); load(); }}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="contract-save-btn">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </Button>
            </>
          )}
        </div>
      </div>

      {!projectId && !editing && (
        <div className="text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-sm p-6 text-center">
          Select a project from the top-right dropdown to view or set up its contract record.
        </div>
      )}

      {(projectId || editing) && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
          {/* Project */}
          {!projectId && editing && (
            <F label="Project *">
              <select value={form.project_id} onChange={upd('project_id')}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#3b4872]">
                <option value="">— Select project —</option>
                {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
              </select>
            </F>
          )}

          {/* Award */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <F label="Contract Type">
              <select value={form.contract_type} onChange={upd('contract_type')} disabled={!editing}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]">
                {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="LOI Reference">{inp('loi_ref')}</F>
            <F label="LOA Reference">{inp('loa_ref')}</F>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <F label="Award Date">{inp('award_date', 'date')}</F>
            <F label="Contract Start">{inp('start_date', 'date')}</F>
            <F label="Completion Date">{inp('completion_date', 'date')}</F>
          </div>

          {/* Value */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <F label="Contract Value (₹)">
              <input type="number" value={form.contract_value || ''} onChange={updNum('contract_value')} disabled={!editing}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]" />
            </F>
            <F label="DLP (months)">{inp('dlp_months', 'number')}</F>
            <F label="Mobilisation Advance (%)">{inp('mobilisation_advance_pct', 'number')}</F>
          </div>

          {/* Commercial terms */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Commercial Terms</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <F label="Retention (%)">
                <input type="number" value={form.retention_pct} onChange={updNum('retention_pct')} disabled={!editing}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]"
                  data-testid="contract-retention-pct" />
              </F>
              <F label="Retention release on completion (%)">
                <input type="number" value={form.retention_release_on_completion_pct} onChange={updNum('retention_release_on_completion_pct')} disabled={!editing}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]" />
              </F>
              <F label="LD Per Day (₹)">
                <input type="number" value={form.ld_per_day || ''} onChange={updNum('ld_per_day')} disabled={!editing}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]" />
              </F>
              <F label="LD Cap (%)">
                <input type="number" value={form.ld_cap_pct} onChange={updNum('ld_cap_pct')} disabled={!editing}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872]" />
              </F>
            </div>
          </div>

          {/* Clauses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Payment Terms">
              <textarea value={form.payment_terms || ''} onChange={upd('payment_terms')} disabled={!editing} rows={2}
                placeholder="e.g. Running bills — 45 days from certification"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872] resize-none" />
            </F>
            <F label="Escalation Clause">
              <textarea value={form.escalation_clause || ''} onChange={upd('escalation_clause')} disabled={!editing} rows={2}
                placeholder="e.g. Steel escalation at ±5% threshold"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872] resize-none" />
            </F>
          </div>
          <F label="Notes">
            <textarea value={form.notes || ''} onChange={upd('notes')} disabled={!editing} rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50 focus:outline-none focus:border-[#3b4872] resize-none" />
          </F>

          {contract?.updated_by && !editing && (
            <div className="text-[10px] text-slate-400">
              Last updated by {contract.updated_by} on {_fmtDate(contract.updated_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
